/**
 * Generic API client with error handling and type safety
 *
 * Provides HTTP methods (GET, POST, PUT, PATCH, DELETE) with:
 * - Automatic error handling and logging
 * - Type-safe responses with Zod validation
 * - Idempotency key support
 * - Request/response envelope unwrapping
 */

import * as Sentry from '@sentry/nextjs';
import type { z } from 'zod';

import { buildHeaders, sanitizeHeaders } from '@/lib/api-headers';
import { AppLogger } from '@/lib/logger';
import { ApiClientError } from '@/src/schemas/api-client-error';
import type {
  RequestLogContext,
  InternalRequestOptions,
  ApiRequestOptions,
} from '@/src/schemas/api-client-types';
import {
  isSuccessEnvelope,
  isErrorEnvelope,
  isLegacyError,
  isGenericError,
} from '@/src/schemas/api-envelopes';

/**
 * Generic API client for making typed HTTP requests.
 *
 * **Features:**
 * - Automatic error handling and logging
 * - Type-safe responses with optional Zod validation
 * - Idempotency key generation for write operations
 * - Request/response envelope unwrapping
 * - Sentry integration for external API calls
 *
 * **Usage:**
 * - Internal API: Use default `apiClient` (baseUrl='', autoIdempotency=true)
 * - External API: Create instance with baseUrl and custom config
 *
 * @example Internal API call
 * ```typescript
 * import { apiClient } from '@/lib/api-client';
 *
 * const widget = await apiClient.get('/api/widgets/123', widgetSchema);
 * ```
 *
 * @example External API with custom config
 * ```typescript
 * const githubClient = new ApiClient('https://api.github.com', true, false);
 * const repos = await githubClient.get('/user/repos', reposSchema);
 * ```
 */
export class ApiClient {
  private baseUrl: string;
  private enableLogging: boolean;
  private autoIdempotency: boolean;

  /**
   * Creates a new API client instance.
   *
   * @param baseUrl - Base URL for all requests (empty string for relative URLs)
   * @param enableLogging - Whether to log requests/responses (default: true)
   * @param autoIdempotency - Whether to auto-generate idempotency keys (default: true for internal, false for external)
   */
  constructor(
    baseUrl: string = '',
    enableLogging: boolean = true,
    autoIdempotency: boolean = baseUrl === ''
  ) {
    this.baseUrl = baseUrl;
    this.enableLogging = enableLogging;
    this.autoIdempotency = autoIdempotency;
  }

  /**
   * Makes a typed API request with automatic error handling and logging.
   *
   * **Error Handling:**
   * - Throws ApiClientError for non-2xx responses
   * - Captures errors in Sentry for external APIs
   * - Logs all requests and responses (sanitizes sensitive headers)
   *
   * **Response Unwrapping:**
   * - Automatically unwraps `{ ok: true, data: T }` envelopes
   * - Handles legacy `{ data: T }` format
   * - Validates response with Zod schema if provided
   *
   * @param endpoint - API endpoint path (appended to baseUrl)
   * @param options - Request options (method, headers, body, etc.)
   * @param schema - Optional Zod schema for response validation
   * @param context - Additional logging context (userId, correlationId, etc.)
   * @returns Parsed and validated response data
   * @throws {ApiClientError} On non-2xx response or network error
   * @throws {z.ZodError} On schema validation failure
   */
  async request<T>(
    endpoint: string,
    options: InternalRequestOptions = {},
    schema?: z.ZodSchema<T>,
    context?: Partial<RequestLogContext>
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const startTime = Date.now();
    const {
      idempotencyKey,
      headers: initialHeaders,
      method: rawMethod,
      ...restOptions
    } = options ?? {};

    const method = (rawMethod || 'GET').toUpperCase();

    const headers = buildHeaders(
      initialHeaders,
      method,
      this.autoIdempotency && !idempotencyKey
    );
    if (idempotencyKey) {
      headers.set('Idempotency-Key', idempotencyKey);
    }
    const headersForLog = sanitizeHeaders(headers);

    const logContext: RequestLogContext = {
      method,
      url,
      ...context,
      ...(headersForLog && { headers: headersForLog }),
    };

    // Log request start
    if (this.enableLogging) {
      AppLogger.info(`API Request: ${method} ${url}`, {
        ...logContext,
      });
    }

    try {
      const response = await fetch(url, {
        method,
        headers,
        ...(restOptions.body && { body: restOptions.body }),
        ...(restOptions.cache && { cache: restOptions.cache }),
        ...(restOptions.credentials && {
          credentials: restOptions.credentials,
        }),
        ...(restOptions.integrity && { integrity: restOptions.integrity }),
        ...(restOptions.keepalive !== undefined && {
          keepalive: restOptions.keepalive,
        }),
        ...(restOptions.mode && { mode: restOptions.mode }),
        ...(restOptions.redirect && { redirect: restOptions.redirect }),
        ...(restOptions.referrer && { referrer: restOptions.referrer }),
        ...(restOptions.referrerPolicy && {
          referrerPolicy: restOptions.referrerPolicy,
        }),
        ...(restOptions.signal && { signal: restOptions.signal }),
        ...(restOptions.window !== undefined && { window: restOptions.window }),
      });

      const duration = Date.now() - startTime;
      logContext.duration = duration;
      logContext.status = response.status;

      // Robust body parsing: handle empty or non-JSON responses gracefully
      const contentType = response.headers.get('content-type') || '';
      let data: unknown;
      if (response.status === 204 || response.status === 205) {
        data = {};
      } else {
        const raw = await response.text();
        if (!raw) {
          data = {};
        } else if (contentType.includes('application/json')) {
          try {
            data = JSON.parse(raw);
          } catch {
            data = {};
          }
        } else {
          // Non-JSON payload; surface as text for debugging where helpful
          data = { raw } as unknown;
        }
      }

      if (!response.ok) {
        // Log error
        if (this.enableLogging) {
          AppLogger.error(
            `API Error: ${method} ${url}`,
            new Error(`HTTP ${response.status}: ${response.statusText}`),
            {
              ...logContext,
              responseData: data,
            }
          );
        }

        let message = `HTTP ${response.status}: ${response.statusText}`;
        let code: string | undefined;
        let details: unknown;

        // Check various error envelope formats using type guards
        if (isLegacyError(data)) {
          message = data.error.message || message;
        } else if (isErrorEnvelope(data)) {
          message = data.message || message;
          code = data.code;
          details = data.details;
        } else if (isGenericError(data)) {
          message = data.message;
          code = data.code;
          details = data.details;
        }

        const errorOptions: {
          status: number;
          code?: string;
          details?: unknown;
          data?: unknown;
        } = {
          status: response.status,
        };

        if (code) {
          errorOptions.code = code;
        }
        if (details !== undefined) {
          errorOptions.details = details;
        }
        if (data !== undefined) {
          errorOptions.data = data;
        }

        throw new ApiClientError(message, errorOptions);
      }

      // Log success
      if (this.enableLogging) {
        AppLogger.info(
          `API Success: ${method} ${url}`,
          logContext as unknown as Record<string, unknown>
        );
      }

      // Unwrap canonical success envelope { ok:true, data }
      let payload: unknown = data;

      if (isSuccessEnvelope(data)) {
        payload = data.data;
      }

      // Validate response with schema if provided
      if (schema) {
        return schema.parse(payload);
      }

      return payload as T;
    } catch (error) {
      const duration = Date.now() - startTime;
      logContext.duration = duration;

      // Log error
      if (this.enableLogging) {
        AppLogger.error(
          `API Request Failed: ${method} ${url}`,
          error as Error,
          logContext as unknown as Record<string, unknown>
        );
      }

      // Capture in Sentry for external API calls
      if (this.baseUrl && error instanceof Error) {
        Sentry.captureException(error, {
          tags: {
            api_client: true,
            method,
            url,
            base_url: this.baseUrl,
          },
          extra: logContext as unknown as Record<string, unknown>,
        });
      }

      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Unknown error occurred');
    }
  }

  /**
   * GET request helper.
   *
   * @param endpoint - API endpoint path
   * @param schema - Optional Zod schema for response validation
   * @param context - Additional logging context
   * @param options - Request options (headers, etc.)
   * @returns Parsed and validated response data
   */
  async get<T>(
    endpoint: string,
    schema?: z.ZodSchema<T>,
    context?: Partial<RequestLogContext>,
    options?: ApiRequestOptions
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        ...(options ?? {}),
        method: 'GET',
      },
      schema,
      context
    );
  }

  /**
   * POST request helper.
   *
   * @param endpoint - API endpoint path
   * @param body - Request body (will be JSON stringified)
   * @param schema - Optional Zod schema for response validation
   * @param context - Additional logging context
   * @param options - Request options (headers, idempotencyKey, etc.)
   * @returns Parsed and validated response data
   */
  async post<T>(
    endpoint: string,
    body: unknown,
    schema?: z.ZodSchema<T>,
    context?: Partial<RequestLogContext>,
    options?: ApiRequestOptions
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        ...(options ?? {}),
        method: 'POST',
        body: JSON.stringify(body),
      },
      schema,
      context
    );
  }

  /**
   * PUT request helper.
   *
   * @param endpoint - API endpoint path
   * @param body - Request body (will be JSON stringified)
   * @param schema - Optional Zod schema for response validation
   * @param context - Additional logging context
   * @param options - Request options (headers, idempotencyKey, etc.)
   * @returns Parsed and validated response data
   */
  async put<T>(
    endpoint: string,
    body: unknown,
    schema?: z.ZodSchema<T>,
    context?: Partial<RequestLogContext>,
    options?: ApiRequestOptions
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        ...(options ?? {}),
        method: 'PUT',
        body: JSON.stringify(body),
      },
      schema,
      context
    );
  }

  /**
   * PATCH request helper.
   *
   * @param endpoint - API endpoint path
   * @param body - Request body (will be JSON stringified)
   * @param schema - Optional Zod schema for response validation
   * @param context - Additional logging context
   * @param options - Request options (headers, idempotencyKey, etc.)
   * @returns Parsed and validated response data
   */
  async patch<T>(
    endpoint: string,
    body: unknown,
    schema?: z.ZodSchema<T>,
    context?: Partial<RequestLogContext>,
    options?: ApiRequestOptions
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        ...(options ?? {}),
        method: 'PATCH',
        body: JSON.stringify(body),
      },
      schema,
      context
    );
  }

  /**
   * DELETE request helper.
   *
   * @param endpoint - API endpoint path
   * @param schema - Optional Zod schema for response validation
   * @param context - Additional logging context
   * @param options - Request options (headers, idempotencyKey, etc.)
   * @returns Parsed and validated response data
   */
  async delete<T>(
    endpoint: string,
    schema?: z.ZodSchema<T>,
    context?: Partial<RequestLogContext>,
    options?: ApiRequestOptions
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        ...(options ?? {}),
        method: 'DELETE',
      },
      schema,
      context
    );
  }
}
